import { Box, makeStyles, Theme } from '@material-ui/core'
import { DataColumn } from '@src/components/DataTable/DataTable'
import DataTable from '@src/components/DataTable'
import { Linq } from '@src/components/Linq'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import { InfoCircleIcon } from '@src/icons'
import TooltipButton from '@src/components/TooltipButton'
import MDDeliveryCol from './MDDeliveryCol'
import MDCommentsCol from './MDCommentsCol'
import { MaterialsDirectItems } from '@src/types/graphql-types'
import { MaterialsDirectQuery } from '@src/queries/MaterialsDirectQuery.generated'
import { orderCenterRoutes } from '@src/routes'
import { sendEvent } from '@src/utils/analytics/coreAnalytics'
import { GTMEventTypes } from '@sial/common-utils'

const useStyles = makeStyles((theme: Theme) => ({
  mdtable: {
    '& .MuiTableCell-body': {
      verticalAlign: 'top !important',
      minHeight: '75px',
      height: '75px',
      fontSize: `${theme.typography.pxToRem(14)} !important`,
    },
  },
  quantityContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  infoIcon: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
  },
  lot: {
    marginBottom: theme.spacing(1.5),
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
  },
  lotIcon: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: theme.typography.pxToRem(10),
    marginTop: theme.spacing(-2),
  },
  dash: {
    textAlign: 'center',
  },
}))

const MDTable = ({ data }: { data: MaterialsDirectQuery }) => {
  const classes = useStyles()
  const { formatNumber } = useIntl()

  const dataColumns: DataColumn[] = [
    {
      headElement: (
        <FormattedMessage id="MATERIAL_NO" defaultMessage="Material No" />
      ),
      bodyElement: (lineItem) => (
        <Linq role="button" aria-hidden="true" href="#">
          {lineItem.materialNo}
        </Linq>
      ),
    },
    {
      headElement: (
        <FormattedMessage id="CUSTOMER_PART" defaultMessage="Customer Part" />
      ),
      bodyElement: (lineItem) => lineItem.customerMaterialNo,
    },
    {
      headElement: (
        <FormattedMessage id="LINE_NUMBER" defaultMessage="Line Number" />
      ),
      bodyElement: (lineItem) => lineItem.itemNo,
      width: '8%',
    },
    {
      headElement: (
        <FormattedMessage id="ORDER_NO_ABBREVIATED" defaultMessage="Order No" />
      ),
      bodyElement: (lineItem) => (
        <Linq
          role="button"
          aria-hidden="true"
          href={`${orderCenterRoutes.orderDetail(lineItem.orderNo)}`}
          onClick={() => {
            sendEvent({
              eventType: GTMEventTypes.AnalyticsEvent,
              payload: {
                event: 'order_interaction',
                action: 'visit order details',
                detail: `${lineItem.orderNo}`,
                section: 'material direct orders',
                order_type:
                  lineItem?.orderType === undefined
                    ? 'standard'
                    : lineItem?.orderType,
                component: 'list',
                element_type: 'link',
                element_text: lineItem.orderNo,
                link_url: `${orderCenterRoutes.orderDetail(lineItem.orderNo)}`,
                core_event: 'no',
                user_detail: undefined,
                event_group: undefined,
                event_subgroup: undefined,
              },
            })
          }}
        >
          {lineItem.orderNo}
        </Linq>
      ),
    },
    {
      headElement: <FormattedMessage id="PO_NO" defaultMessage="PO No" />,
      bodyElement: (lineItem) => lineItem.poNo,
    },
    {
      headElement: (
        <Box className={classes.quantityContainer}>
          <FormattedMessage id="QUANTITY" defaultMessage="Quantity" />
          <TooltipButton
            leaveDelay={0}
            toolTipText={
              <FormattedMessage
                id="ORDERED_CONFIRMED"
                defaultMessage="Ordered/Confirmed"
              />
            }
            showOutline={false}
          >
            <InfoCircleIcon className={classes.infoIcon} />
          </TooltipButton>
        </Box>
      ),
      bodyElement: (lineItem) =>
        `${formatNumber(lineItem.qty.ordered)}/${formatNumber(lineItem.qty.confirmed)}`,
    },
    {
      headElement: (
        <FormattedMessage id="ORDER_DATE" defaultMessage="Order Date" />
      ),
      bodyElement: (lineItem) => (
        <FormattedDate
          value={lineItem.orderedDate}
          year="numeric"
          month="short"
          day="numeric"
        />
      ),
    },
    {
      headElement: (
        <FormattedMessage id="REQUEST_DATE" defaultMessage="Request Date" />
      ),
      bodyElement: (lineItem) =>
        lineItem.custReqDate ? (
          <FormattedDate
            value={lineItem.custReqDate}
            year="numeric"
            month="short"
            day="numeric"
          />
        ) : (
          <div className={classes.dash}>-</div>
        ),
    },
    {
      headElement: (
        <FormattedMessage id="EST_AVAIL_DATE" defaultMessage="Est Avail Date" />
      ),
      bodyElement: (lineItem) =>
        lineItem.estShipDate ? (
          <FormattedDate
            value={lineItem.estShipDate}
            year="numeric"
            month="short"
            day="numeric"
          />
        ) : (
          <div className={classes.dash}>-</div>
        ),
    },
    {
      headElement: <FormattedMessage id="STATUS" defaultMessage="Status" />,
      bodyElement: (lineItem) => lineItem.status,
    },
    {
      headElement: <FormattedMessage id="COMMENTS" defaultMessage="Comments" />,
      bodyElement: (lineItem) => {
        return <MDCommentsCol item={lineItem} />
      },
      width: '7%',
    },

    {
      headElement: <FormattedMessage id="DELIVERY" defaultMessage="Delivery" />,
      bodyElement: (lineItem) => <MDDeliveryCol item={lineItem} />,
      width: '5%',
    },
  ]

  return (
    <DataTable
      className={classes.mdtable}
      data={data.fetchMaterialsDirectOrders.items as MaterialsDirectItems[]}
      columns={dataColumns}
      captionText={
        <FormattedMessage
          id="MATERIALS_DIRECT"
          defaultMessage="Materials Direct"
        />
      }
      noDataComponent={
        <FormattedMessage
          id="NO_RESULTS_FOUND"
          defaultMessage="No results found"
        />
      }
    />
  )
}

export default MDTable
